import { useState, useRef, useEffect } from 'react';
import useCancellablePromise from '../../../../hooks/useCancellablePromise';
import { listen, abortListen } from 'Services/Listen';
import { logSessionInfo } from '../../../../services/logger';

const INFERENCE_STALL_TRIGGER_TIMEOUT = 3375;
const INFERENCE_TIMEOUT = 6000;

// TODO: useInteractionController should implement this
export default function useComprehensionInference({ storyId, activityId, phraseIndex, speech }) {
  // State
  const [transcript, setTranscript] = useState(null);

  // Hooks
  const { cancellableCPromise } = useCancellablePromise();

  // Refs
  const inferenceStartTimeRef = useRef(null);
  const inferencePromiseRef = useRef(null);

  const startInferenceTimeTracker = () => {
    const now = Date.now();
    inferenceStartTimeRef.current = now;
  };

  const endInferenceTimeTracker = () => {
    const now = Date.now();
    const timeElapsed = now - inferenceStartTimeRef.current;
    const timeElapsedSeconds = timeElapsed / 1000;
    const payload = { 
      timestamp: now,
      timeEllapsed: `${timeElapsedSeconds.toFixed(3)} seconds`,
      timeEllapsedMs: timeElapsed,
    };
    logSessionInfo("comprehension_inference_received_by_component", payload);
    console.log("Comprehension Dialog: comprehension_inference_received_by_component", payload);
    inferenceStartTimeRef.current = null;
  };

  const listenCallback = (args, newTranscript) => {
    // Don't set transcript back to null if it is not null and newTranscript is null
    if ((transcript && newTranscript) || (!transcript && newTranscript)) {
      setTranscript(newTranscript);
    }
  }

  const onInference = (data, onSuccess, onError) => {
    endInferenceTimeTracker();
    if (!data.error) {
      onSuccess && onSuccess(data);
    } else {
      onError && onError(data.error);
    }
  }

  const startListening = (audioDestination) => {
    try {
      const promise = new Promise((resolve) => {
        listen({
          callbackOverride: listenCallback,
          speech,
          audioDestination,
          useComprehension: true,
          storyId,
          phraseIndex,
          activityId,
          callback: (data) => resolve(onInference(data)),
        });
      });

      inferencePromiseRef.current = cancellableCPromise(promise);
    } catch(error) {
      inferencePromiseRef.current = new Promise.resolve({ error });
    }
  }

  useEffect(() => {
    return () => {
      speech.stopListening();
      abortListen(
        speech,
        true // endOfPhrase
      )
    }
  }, []);

  const triggerInference = (payload, onSuccess, onError, comprehensionDone = true) => {
    startInferenceTimeTracker();

    if (comprehensionDone) {
      payload.comprehensionDone = true;
    } else {
      payload.comprehensionDiscard = true;
    }

    logSessionInfo("comprehension_audio_inference_requested", payload);
    console.log("Comprehension Inference: Sending audio response to inference (comprehensionDone: true): ", payload);
    
    speech.disableRecording(comprehensionDone, payload);
    speech.stopListening();

    let onStallFinish = () => {};

    let loadingTimeout = setTimeout(() => {
      onStallFinish = () => {
          // Trigger amira lil pose
          speech.playDance({gesture: amiraGestures.Thinking}, () => {});
          const textToSpeak = STRINGS.TIME_STALLS[Math.floor(Math.random() * STRINGS.TIME_STALLS.length)];
          speech.speakWithPromise({ textToSpeak, useGenerative: true })
      }
    }, INFERENCE_STALL_TRIGGER_TIMEOUT);

    let inferenceTimeout = setTimeout(() => {
      logSessionInfo('comprehension_inference_timeout', {});
      console.log('Comprehension Dialog: comprehension_inference_timeout occurred.');
      onError && onError({timeout: true});
    }, INFERENCE_TIMEOUT);

    inferencePromiseRef.current.promise.then(data => {
      clearTimeout(inferenceTimeout);
      clearTimeout(loadingTimeout);
      onSuccess && onSuccess(data);
    }).catch(e => {
      console.error("Comprehension Dialog: ERROR in comp callback", e);
      clearTimeout(loadingTimeout);
      clearTimeout(inferenceTimeout);
      onError && onError();
    })
  }

  return {
    startListening,
    transcript,
    triggerInference,
  }
}